import { faGripLines } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '@hl/ui-components';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useIsMobile } from '../../utils/useIsMobile';
import {
  PanelOverlayWrapper,
  ResizeHandleWrapper,
} from './PanelOverlayWrapper.styled';

export interface IPanelOverlayProps {
  className?: string;
  show: boolean;
  stopAnimation?: boolean;
  children?: ReactNode;
  initialWidth?: number; // in pixels
  minWidth?: number; // in pixels
  maxWidth?: number; // in pixels
  resizable?: boolean;
  onResize?: (width: number) => void;
}

const minLeftGap = 40; // the minimum gap between the left edge of the panel and the left edge of the screen
const hiddenRightOffset = -25; // the number of pixels the panel is hidden off the right edge of the screen to hide the shadow

export const PanelOverlay = ({
  className,
  initialWidth = 400,
  minWidth = 250,
  maxWidth = 1200,
  resizable = true,
  stopAnimation = false,
  ...props
}: IPanelOverlayProps) => {
  const [currentRightOffset, setCurrentRightOffset] = useState<number>(
    hiddenRightOffset - initialWidth,
  );
  const [desiredWidth, setDesiredWidth] = useState<number>(initialWidth); // the width the panel should be, but it may not be the actual width due to min/max constraints
  const [width, setWidth] = useState<number>(initialWidth);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [resizingMouseDelta, setResizingMouseDelta] = useState<number>(0); // the difference between the mouse and the left edge of the panel

  const isMobile = useIsMobile(); // track isMobile in state (rather than with a function call to getIsMobile) to guarantee component re-renders when isMobile changes

  const correctWidth = useCallback(
    (width: number) => {
      if (isMobile) {
        return window.innerWidth;
      }
      if (width < minWidth) {
        return minWidth;
      }
      if (width > maxWidth) {
        return maxWidth;
      }
      if (width > window.innerWidth - minLeftGap) {
        return window.innerWidth - minLeftGap;
      }
      return width;
    },
    [isMobile, maxWidth, minWidth],
  );

  const setNewWidth = useCallback(
    (newWidth: number) => {
      setDesiredWidth(newWidth);
      setWidth(newWidth);
      props.onResize?.(newWidth);
    },
    [props],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!resizable || isMobile) return;

      const step = e.shiftKey ? 50 : 10;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          setNewWidth(correctWidth(width + step));
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          setNewWidth(correctWidth(width - step));
          break;
        default:
          return;
      }
    },
    [resizable, isMobile, setNewWidth, correctWidth, width],
  );

  const handleMouseMove = (e: MouseEvent) => {
    if (resizable && isResizing) {
      const cursorWidth = correctWidth(
        window.innerWidth - e.clientX - resizingMouseDelta,
      );
      setDesiredWidth(cursorWidth);
      setWidth(cursorWidth);
      props.onResize?.(cursorWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  const handleWindowResize = () => {
    if (props.show) {
      setWidth(correctWidth(desiredWidth));
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  useEffect(() => {
    if (props.show) {
      setCurrentRightOffset(0);
    } else {
      setCurrentRightOffset(hiddenRightOffset - width);
    }
  }, [props.show, width]);

  useEffect(() => {
    const correctedWidth = correctWidth(desiredWidth);
    setWidth(correctedWidth);
    if (!props.show) {
      setCurrentRightOffset(hiddenRightOffset - correctedWidth);
    }
  }, [minWidth, maxWidth, correctWidth, desiredWidth, props.show]);

  const maybeGetResizeHandle = () =>
    resizable &&
    !isMobile && (
      <>
        <ResizeHandleWrapper
          className="draggable-resize"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
            setResizingMouseDelta(window.innerWidth - width - e.clientX);
          }}
          aria-hidden="true"
          tabIndex={-1}
        />
        <ResizeHandleWrapper
          className="tab-button-resize"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
            setResizingMouseDelta(window.innerWidth - width - e.clientX);
          }}
          onKeyDown={handleKeyDown}
          aria-controls="panel-overlay-wrapper"
          aria-label="Drag to resize panel or use arrow keys to resize (hold Shift for larger steps)."
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          aria-valuenow={width}
          aria-valuetext={`Panel width ${width} pixels.`}
          role="slider"
          aria-orientation="horizontal"
          tabIndex={0}
        >
          <Icon icon={faGripLines} style={{ transform: 'rotate(90deg)' }} />
        </ResizeHandleWrapper>
      </>
    );

  const innerPanel = (
    <PanelOverlayWrapper
      className={className}
      $rightOffset={currentRightOffset}
      $stopAnimation={isMobile || isResizing || stopAnimation}
      id={'panel-overlay-wrapper'}
      $width={width}
    >
      {maybeGetResizeHandle()}
      {props.children}
    </PanelOverlayWrapper>
  );

  return createPortal(innerPanel, window.document.body);
};
